import React, { useState, useEffect, useRef } from 'react';
import { registry } from '../../core/Registry.js';
import { BaseWidget } from '../BaseWidget.jsx';

export function CounterRender({ node, isLiveMode }) {
  const content = node.content || {};
  
  const targetNumber = parseFloat(content.targetNumber) || 100;
  const prefix = content.prefix || '';
  const suffix = content.suffix || '%';
  const duration = parseInt(content.duration, 10) || 2000; // ms
  const title = content.title || 'Kundenzufriedenheit';
  
  const [count, setCount] = useState(isLiveMode ? 0 : targetNumber);
  const containerRef = useRef(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    if (!isLiveMode) return; // Im Editor nicht wiederholt animieren
    
    // Intersection Observer um Animation erst beim Scrollen auszulösen
    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry.isIntersecting && !hasAnimated) {
        setHasAnimated(true);
        startAnimation();
      }
    }, { threshold: 0.1 });

    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [isLiveMode, hasAnimated, targetNumber, duration]);

  const startAnimation = () => {
    let startTimestamp = null;
    const step = (timestamp) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      
      // Easing function (easeOutExpo)
      const easeProgress = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      
      setCount(easeProgress * targetNumber);
      
      if (progress < 1) {
        window.requestAnimationFrame(step);
      } else {
        setCount(targetNumber);
      }
    };
    window.requestAnimationFrame(step);
  };

  // Anzeige runden (Ganzzahl oder Nachkommastelle je nach Input)
  const displayCount = targetNumber % 1 !== 0 ? count.toFixed(1) : Math.floor(count);

  return (
    <div ref={containerRef} style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      background: 'rgba(255,255,255,0.02)',
      borderRadius: '16px',
      border: '1px solid rgba(255,255,255,0.05)',
      width: '100%',
      textAlign: 'center'
    }}>
      <div style={{
        fontSize: '48px',
        fontWeight: '900',
        color: 'var(--tef-primary, #00A9E0)',
        lineHeight: 1,
        marginBottom: '8px',
        fontVariantNumeric: 'tabular-nums' // verhindert Wackeln bei Zahlenwechsel
      }}>
        {prefix}{displayCount}{suffix}
      </div>
      <div style={{ fontSize: '16px', color: '#cbd5e1', fontWeight: '500' }}>
        {title}
      </div>
    </div>
  );
}

registry.register('Counter', {
  label: 'Zähler (Counter)',
  icon: '⏱️', 
  category: 'advanced',
  component: CounterRender,
  
  defaultProps: {
    content: {
      targetNumber: '99.9',
      prefix: '',
      suffix: '%',
      title: 'Netzabdeckung in Deutschland',
      duration: '2000'
    },
    style: {
      desktop: {
        marginBottom: '20px'
      }
    }
  },

  inspectorControls: [
    {
      tab: 'content',
      groups: [
        {
          title: 'Werte',
          fields: [
            { name: 'content.targetNumber', label: 'Ziel-Zahl', type: 'text' },
            { name: 'content.title', label: 'Beschriftung', type: 'text' }
          ]
        },
        {
          title: 'Formatierung',
          fields: [
            { name: 'content.prefix', label: 'Präfix (z.B. + oder $)', type: 'text' },
            { name: 'content.suffix', label: 'Suffix (z.B. % oder Mbit)', type: 'text' },
            { name: 'content.duration', label: 'Dauer in ms (z.B. 2000)', type: 'text' }
          ]
        }
      ]
    }
  ]
});
